import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Form,
  Popover,
  Space,
  Spin,
  Switch,
  Select,
  notification,
} from "antd";
import { ArrowLeft, ChevronDown, Info, Tags, TriangleAlert } from "lucide-react";
import { z } from "zod";
import usePageTitle from "../../core/hooks/UsePageTitle";
import DefaultLayout from "../layouts/default/Default.layout";
import useMaterias from "../../core/hooks/UseMaterias";
import useBancas from "../../core/hooks/UseBanca";
import CertameService from "../../sdk/services/CertameService";
import { Certame } from "../../sdk/@types";
import {
  FilterChip,
  FilterChipGroup,
  FilterSearch,
  FilterSection,
  chipClassName,
} from "../components/filters";
import { CertameEditorShell } from "../components/CertameEditorShell/CertameEditorShell";
import TagVisibilityBar from "../components/TagVisibilityBar/TagVisibilityBar";

/* ===================== SCHEMA =====================
 * Same shape, same messages as the Drawer form this page replaces
 * (src/app/views/Certame.view.tsx) — the editor tasks that build Matérias/
 * Bancas/Tags on top of this shell reuse it, so it stays here rather than
 * duplicated per section. */

const materiaSchema = z.object({
  id: z.string({ error: "O ID da matéria é obrigatório" }),
});

const certameSchema = z.object({
  id: z.string().optional(),

  abbreviation: z
    .string({ error: "A sigla é obrigatória" })
    .min(3, "A sigla deve ter pelo menos 3 caracteres"),

  description: z
    .string({ error: "A descrição é obrigatória" })
    .min(6, "A descrição deve ter pelo menos 6 caracteres"),

  disabled: z.boolean({ error: "O campo 'disabled' é obrigatório" }),

  createdAt: z.coerce
    .date()
    .transform((d) => d.toISOString())
    .optional(),

  subjectIds: z
    .array(z.string({ error: "O ID da matéria é obrigatório" }))
    .min(1, "Informe pelo menos uma matéria"),

  // Sem `min(1)`, ao contrário de subjectIds: lista vazia é estado válido e
  // significa "sem restrição de banca".
  testingAgencyIds: z.array(z.string()).optional(),

  subjects: z.array(materiaSchema).optional(),
});

type CertameFormInput = z.input<typeof certameSchema>;
type CertameFormOutput = z.output<typeof certameSchema>;

const emptyDefaults: CertameFormInput = {
  id: "",
  abbreviation: "",
  description: "",
  disabled: false,
  subjectIds: [],
  testingAgencyIds: [],
  subjects: [],
};

const numberFormatter = new Intl.NumberFormat("pt-BR");

// Mesmo corte usado em outras telas para ids longos: início + fim, sem o
// meio, o suficiente para distinguir visualmente sem ocupar a linha.
function abbreviateId(id: string): string {
  if (id.length <= 12) return id;
  return `${id.slice(0, 6)}…${id.slice(-4)}`;
}

type TagsFilterMode = "all" | "visible" | "hidden" | "review";

// Mesmo corte do tier "inline" de FilterOverflow (components/filters/tokens.ts) —
// acima disso a matéria ganha o link "Mostrar mais N tags" (design.md, Decisão 3).
const CHIP_DISPLAY_THRESHOLD = 24;

// Chave do mapa de alterações pendentes de visibilidade — uma tag só existe
// dentro de uma matéria, então o par (subjectId, tag) identifica a entrada.
const tagChangeKey = (subjectId: string, tag: string) => `${subjectId}:::${tag}`;

interface PendingTagChange {
  subjectId: string;
  tag: string;
  previousVisible: boolean;
}

function TagCounterTile({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: number;
  tone?: "neutral" | "warn";
}) {
  return (
    <div
      className={`flex-1 min-w-[150px] rounded-card border px-4 py-3 ${
        tone === "warn"
          ? "border-warn/[0.42] bg-warn/[0.08]"
          : "border-edge bg-surface-muted"
      }`}
    >
      <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
        {label}
      </p>
      <p
        className={`mt-1 text-xl font-semibold tabular-nums ${
          tone === "warn" ? "text-warn-ink" : "text-ink"
        }`}
      >
        {numberFormatter.format(value)}
      </p>
    </div>
  );
}

/* ===================== COMPONENT ===================== */

export default function CertameEditarView() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();

  usePageTitle(isNew ? "Novo certame" : "Editar certame");

  const { materias, fetchMaterias } = useMaterias();
  // Catálogo completo (`/testingagencies`), e não a rota escopada por
  // assinante: admin não tem registro de assinante, e aqui ele precisa
  // justamente de todas as bancas para declarar quais pertencem ao certame.
  const { bancas, fetchBancas } = useBancas();

  const [loading, setLoading] = useState(!isNew);

  // Matérias vinculadas: contagem de tags vem do painel de tags do certame
  // (bySubject[].totalTags, já carregado ali — ver design.md); não existe
  // endpoint de contagem de questões por matéria, então o total do certame
  // (CertameService.getQuestionCount) é usado como aproximação enquanto esse
  // endpoint não existir (lacuna registrada na tarefa, não inventada aqui).
  const [questionCount, setQuestionCount] = useState<number | null>(null);

  // Painel de saúde das tags (contadores + alerta + filtro/busca) + a
  // alternância individual/por matéria desta tarefa.
  const [tagPanel, setTagPanel] = useState<Certame.TagPanel | null>(null);
  const [tagPanelLoading, setTagPanelLoading] = useState(false);
  const [duplicateTagCount, setDuplicateTagCount] = useState<number | null>(
    null,
  );
  const [commentSnippetTagCount, setCommentSnippetTagCount] = useState<
    number | null
  >(null);
  const [tagsFilterMode, setTagsFilterMode] = useState<TagsFilterMode>("all");
  const [tagsSearch, setTagsSearch] = useState("");
  // Matérias cujo "Mostrar mais N tags" já foi clicado — uma vez expandida,
  // o controle não reaparece (critério 2).
  const [expandedTagChips, setExpandedTagChips] = useState<Set<string>>(
    new Set(),
  );

  // Busca própria de cada matéria, independente da busca global do painel —
  // chaveada por subjectId, uma entrada por matéria (design.md, Decisão 2).
  const [subjectSearchTerms, setSubjectSearchTerms] = useState<
    Record<string, string>
  >({});

  // Acordeão por matéria — qual seção está com o grid de chips aberto.
  // Semeado com só a primeira matéria do painel ao carregar; cada clique
  // de cabeçalho alterna a presença do id neste conjunto.
  const [expandedSubjectIds, setExpandedSubjectIds] = useState<Set<string>>(
    new Set(),
  );

  // Alterações de visibilidade feitas nesta sessão, ainda não confirmadas
  // pelo admin — alimentam o contador da barra de ações e o "Cancelar", que
  // as desfaz chamando `setTagVisibility` de volta ao valor original.
  const [pendingTagChanges, setPendingTagChanges] = useState<
    Map<string, PendingTagChange>
  >(new Map());
  const [cancelling, setCancelling] = useState(false);
  // Fotografia da visibilidade no momento em que o painel foi carregado —
  // é contra ela que cada clique decide se ainda há algo pendente.
  const originalVisibilityRef = useRef<Map<string, boolean>>(new Map());

  const {
    control,
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isSubmitting, dirtyFields },
  } = useForm<CertameFormInput>({
    resolver: zodResolver(certameSchema),
    defaultValues: emptyDefaults,
  });

  const watchedSubjectIds = useWatch({ control, name: "subjectIds" });
  const subjectIds = useMemo(
    () => watchedSubjectIds ?? [],
    [watchedSubjectIds],
  );

  const materiasById = useMemo(() => {
    const map = new Map<string, { id?: string; name?: string }>();
    (materias ?? []).forEach((m) => {
      if (m.id) map.set(m.id, m);
    });
    return map;
  }, [materias]);

  const tagCountBySubject = useMemo(() => {
    const map = new Map<string, number>();
    (tagPanel?.bySubject ?? []).forEach((s) => {
      if (s.subjectId) map.set(s.subjectId, s.totalTags ?? 0);
    });
    return map;
  }, [tagPanel]);

  const availableMaterias = useMemo(
    () =>
      (materias ?? [])
        .filter((m) => m.id && !subjectIds.includes(m.id))
        .map((m) => ({ label: m.name ?? m.id, value: m.id as string })),
    [materias, subjectIds],
  );

  // Soma única de alterações não salvas — cada campo do formulário tocado
  // (react-hook-form já dedupe por nome de campo) mais cada tag cuja
  // visibilidade ainda difere da fotografia do último carregamento.
  const unsavedChangesCount =
    Object.keys(dirtyFields).length + pendingTagChanges.size;

  useEffect(() => {
    fetchMaterias();
    fetchBancas();
  }, [fetchMaterias, fetchBancas]);

  // Carrega sempre da API, nunca de estado herdado da lista — é o que faz um
  // reload direto em /certame/:id funcionar (ver spec "Página dedicada de
  // edição, independente de navegação prévia").
  useEffect(() => {
    if (isNew) {
      reset(emptyDefaults);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    CertameService.getById(id)
      .then((row) => {
        if (cancelled) return;

        reset({
          id: row.id ?? "",
          abbreviation: row.abbreviation ?? "",
          description: row.description ?? "",
          disabled: row.disabled ?? false,
          subjectIds: row.subjectIds ?? [],
          testingAgencyIds: row.testingAgencyIds ?? [],
          subjects: row.subjects ?? [],
          createdAt: row.createdAt ? new Date(row.createdAt) : undefined,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        notification.error({
          message: "Erro ao carregar certame",
          description:
            error instanceof Error ? error.message : "Erro inesperado",
        });
        navigate("/certame");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  // Contagem de questões do certame — aproximação do total por matéria
  // enquanto não existir endpoint de contagem por matéria (ver comentário
  // acima, na seção Matérias vinculadas).
  useEffect(() => {
    if (isNew || !id) {
      setQuestionCount(null);
      return;
    }

    let cancelled = false;

    CertameService.getQuestionCount(id)
      .then((res) => {
        if (!cancelled) setQuestionCount(res.total ?? 0);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setQuestionCount(null);
      });

    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  // Painel de tags: dataset próprio, carregado só quando o certame já existe
  // (não há tags para revisar num certame ainda não salvo). As duas listas
  // de classificação (duplicadas / trechos de comentário) só alimentam o
  // texto do alerta — a tela de revisão em si é outra rota.
  useEffect(() => {
    if (isNew || !id) {
      setTagPanel(null);
      setDuplicateTagCount(null);
      setCommentSnippetTagCount(null);
      originalVisibilityRef.current = new Map();
      setPendingTagChanges(new Map());
      setExpandedSubjectIds(new Set());
      return;
    }

    let cancelled = false;
    setTagPanelLoading(true);

    CertameService.getTagPanel(id)
      .then((panel) => {
        if (cancelled) return;
        setTagPanel(panel);
        setExpandedSubjectIds(
          new Set(panel.bySubject[0] ? [panel.bySubject[0].subjectId] : []),
        );

        const snapshot = new Map<string, boolean>();
        panel.bySubject.forEach((subject) =>
          subject.tags.forEach((tag) => {
            snapshot.set(tagChangeKey(subject.subjectId, tag.value), tag.visible);
          }),
        );
        originalVisibilityRef.current = snapshot;
        setPendingTagChanges(new Map());
      })
      .catch((error) => {
        if (cancelled) return;
        setTagPanel(null);
        notification.error({
          message: "Erro ao carregar o painel de tags",
          description:
            error instanceof Error ? error.message : "Erro inesperado",
        });
      })
      .finally(() => {
        if (!cancelled) setTagPanelLoading(false);
      });

    Promise.all([
      CertameService.getDuplicateTagGroups(id),
      CertameService.getCommentSnippetTags(id),
    ])
      .then(([duplicates, snippets]) => {
        if (cancelled) return;
        setDuplicateTagCount(
          (duplicates?.groups ?? []).reduce(
            (sum, group) => sum + (group.variants?.length ?? 0),
            0,
          ),
        );
        setCommentSnippetTagCount((snippets?.items ?? []).length);
      })
      .catch(() => {
        // A classificação detalhada é só o texto do alerta — se essas duas
        // rotas falharem (ex.: backend ainda não landou), o alerta continua
        // aparecendo pelo needsReviewCount, só sem o detalhamento.
        if (cancelled) return;
        setDuplicateTagCount(null);
        setCommentSnippetTagCount(null);
      });

    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  const addSubject = (subjectId: string) => {
    if (!subjectId || subjectIds.includes(subjectId)) return;
    const next = [...subjectIds, subjectId];
    setValue("subjectIds", next, { shouldValidate: true, shouldDirty: true });
  };

  const removeSubject = (subjectId: string) => {
    const next = subjectIds.filter((s) => s !== subjectId);
    setValue("subjectIds", next, { shouldValidate: true, shouldDirty: true });
  };

  const filteredTagsBySubject = useMemo(() => {
    const term = tagsSearch.trim().toLowerCase();

    return (tagPanel?.bySubject ?? [])
      .map((subject) => ({
        ...subject,
        tags: subject.tags.filter((tag) => {
          // As três faixas dos contadores são mutuamente exclusivas
          // (visíveis + ocultas + precisam-de-revisão = total), então
          // "precisa de revisão" sai de visíveis/ocultas mesmo que a tag
          // também carregue `visible: true|false`.
          if (tagsFilterMode === "visible" && (!tag.visible || tag.needsReview)) {
            return false;
          }
          if (tagsFilterMode === "hidden" && (tag.visible || tag.needsReview)) {
            return false;
          }
          if (tagsFilterMode === "review" && !tag.needsReview) return false;
          if (term && !tag.value.toLowerCase().includes(term)) return false;
          return true;
        }),
      }))
      .filter((subject) => subject.tags.length > 0);
  }, [tagPanel, tagsFilterMode, tagsSearch]);

  const toggleSubjectExpanded = useCallback((subjectId: string) => {
    setExpandedSubjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(subjectId)) {
        next.delete(subjectId);
      } else {
        next.add(subjectId);
      }
      return next;
    });
  }, []);

  // Digitar um termo na busca de uma matéria recolhida deve expandi-la
  // (spec "Typing into a collapsed matéria's search expands it") — nunca o
  // inverso: um termo esvaziado não recolhe a seção.
  useEffect(() => {
    const idsWithTerm = Object.entries(subjectSearchTerms)
      .filter(([, term]) => term.trim().length > 0)
      .map(([subjectId]) => subjectId);
    if (idsWithTerm.length === 0) return;

    setExpandedSubjectIds((prev) => {
      const missing = idsWithTerm.filter((id) => !prev.has(id));
      if (missing.length === 0) return prev;
      const next = new Set(prev);
      missing.forEach((id) => next.add(id));
      return next;
    });
  }, [subjectSearchTerms]);

  // Aplica a mudança de visibilidade no estado local sem esperar a resposta
  // da API (revertido se a chamada falhar) — mantém a invariante
  // visíveis + ocultas + precisam-de-revisão = total, por isso tags
  // marcadas `needsReview` não movem os dois primeiros contadores.
  const applyVisibilityLocally = useCallback(
    (subjectId: string, tagValues: string[], visible: boolean) => {
      setTagPanel((prev) => {
        if (!prev) return prev;
        let visibleDelta = 0;

        const bySubject = prev.bySubject.map((subject) => {
          if (subject.subjectId !== subjectId) return subject;

          let subjectVisibleDelta = 0;
          const tags = subject.tags.map((tag) => {
            if (!tagValues.includes(tag.value) || tag.visible === visible) {
              return tag;
            }
            if (!tag.needsReview) {
              subjectVisibleDelta += visible ? 1 : -1;
            }
            return { ...tag, visible };
          });

          visibleDelta += subjectVisibleDelta;
          return {
            ...subject,
            tags,
            visibleTags: subject.visibleTags + subjectVisibleDelta,
          };
        });

        return {
          ...prev,
          bySubject,
          visibleCount: prev.visibleCount + visibleDelta,
          hiddenCount: prev.hiddenCount - visibleDelta,
        };
      });
    },
    [],
  );

  const updatePendingTagChanges = useCallback(
    (subjectId: string, tagValues: string[], nextVisible: boolean) => {
      setPendingTagChanges((prev) => {
        const next = new Map(prev);
        tagValues.forEach((tagValue) => {
          const key = tagChangeKey(subjectId, tagValue);
          const original = originalVisibilityRef.current.get(key);
          if (original === undefined) return;
          if (original === nextVisible) {
            next.delete(key);
          } else {
            next.set(key, { subjectId, tag: tagValue, previousVisible: original });
          }
        });
        return next;
      });
    },
    [],
  );

  // O mesmo valor de tag pode aparecer em mais de uma matéria vinculada ao
  // certame (ver spec "Tag compartilhada por mais de uma matéria") — alterna
  // a visibilidade a partir de qualquer uma delas tem de valer nas demais,
  // numa única ação do admin. Por isso o alvo não é a matéria clicada, e sim
  // toda matéria de `tagPanel.bySubject` cujo valor de tag casa, cada uma
  // recebendo seu próprio `setTagVisibility` em paralelo — e se qualquer uma
  // falhar, a reversão otimista cobre todas as matérias afetadas, não só a
  // que falhou (spec "Falha ao ocultar em uma das matérias afetadas").
  const toggleTagVisibility = async (tag: Certame.TagEntry) => {
    if (!id || !tagPanel) return;
    const nextVisible = !tag.visible;

    const affected = tagPanel.bySubject
      .map((subject) => {
        const match = subject.tags.find((t) => t.value === tag.value);
        return match
          ? { subjectId: subject.subjectId, previousVisible: match.visible }
          : null;
      })
      .filter(
        (entry): entry is { subjectId: string; previousVisible: boolean } =>
          entry !== null,
      );

    if (affected.length === 0) return;

    affected.forEach(({ subjectId }) => {
      applyVisibilityLocally(subjectId, [tag.value], nextVisible);
      updatePendingTagChanges(subjectId, [tag.value], nextVisible);
    });

    try {
      await Promise.all(
        affected.map(({ subjectId }) =>
          CertameService.setTagVisibility(id, {
            subjectId,
            tags: [tag.value],
            visible: nextVisible,
          }),
        ),
      );
    } catch (error) {
      affected.forEach(({ subjectId, previousVisible }) => {
        applyVisibilityLocally(subjectId, [tag.value], previousVisible);
        updatePendingTagChanges(subjectId, [tag.value], previousVisible);
      });
      notification.error({
        message: "Erro ao atualizar a visibilidade da tag",
        description:
          error instanceof Error ? error.message : "Erro inesperado",
      });
    }
  };

  const setSubjectTagsVisibility = async (
    subjectId: string,
    visible: boolean,
  ) => {
    if (!id || !tagPanel) return;
    const subject = tagPanel.bySubject.find((s) => s.subjectId === subjectId);
    if (!subject) return;

    const targets = subject.tags
      .filter((tag) => tag.visible !== visible)
      .map((tag) => tag.value);
    if (targets.length === 0) return;

    applyVisibilityLocally(subjectId, targets, visible);
    updatePendingTagChanges(subjectId, targets, visible);

    try {
      // `targets` é a lista completa já calculada acima para o estado otimista.
      // Mandá-la é obrigatório: a API exige um item por tag e não tem o
      // "omitir = todas" que este call site presumia.
      await CertameService.setTagVisibility(id, { subjectId, tags: targets, visible });
    } catch (error) {
      applyVisibilityLocally(subjectId, targets, !visible);
      updatePendingTagChanges(subjectId, targets, !visible);
      notification.error({
        message: visible
          ? "Erro ao mostrar as tags da matéria"
          : "Erro ao ocultar as tags da matéria",
        description:
          error instanceof Error ? error.message : "Erro inesperado",
      });
    }
  };

  // Desfaz as alternâncias de visibilidade feitas nesta sessão (cada uma já
  // foi persistida no clique — ver toggleTagVisibility) e volta para a
  // lista, descartando também os campos do formulário que nunca chegaram a
  // ser enviados.
  const handleCancel = async () => {
    if (id && pendingTagChanges.size > 0) {
      setCancelling(true);
      await Promise.all(
        Array.from(pendingTagChanges.values()).map(
          ({ subjectId, tag, previousVisible }) =>
            CertameService
              .setTagVisibility(id, {
                subjectId,
                tags: [tag],
                visible: previousVisible,
              })
              .catch((error) => console.error(error)),
        ),
      );
    }
    navigate("/certame");
  };

  const onSubmit = async (data: CertameFormInput) => {
    try {
      const parsed: CertameFormOutput = certameSchema.parse(data);

      if (isNew) {
        await CertameService.create(parsed as Certame.Certame);
        notification.success({
          message: "Sucesso",
          description: "Certame criado com sucesso.",
        });
      } else {
        await CertameService.update(parsed as Certame.Certame);
        notification.success({
          message: "Sucesso",
          description: "Certame atualizado com sucesso.",
        });
      }

      navigate("/certame");
    } catch (error) {
      if (error instanceof z.ZodError) {
        notification.error({
          message: "Erro de validação",
          description: error.issues.map((e) => e.message).join(", "),
        });
      } else if (error instanceof Error) {
        notification.error({ message: "Erro", description: error.message });
      } else {
        notification.error({
          message: "Erro",
          description: "Erro inesperado",
        });
      }
    }
  };

  const header = (
    <div className="justify-start mb-2 border-b border-edge p-2 w-full flex items-center gap-3">
      <Button
        type="text"
        icon={<ArrowLeft size={18} />}
        onClick={() => navigate("/certame")}
        aria-label="Voltar para a lista de certames"
      />
      <div>
        <p className="text-2xl font-bold text-ink">
          {isNew ? "Novo certame" : "Editar certame"}
        </p>
        <p className="text-base font-normal text-ink-secondary">
          Dados do certame, matérias, bancas e tags vinculadas
        </p>
      </div>
    </div>
  );

  const dadosDoCertame = (
    <div>
      <p className="text-lg font-semibold text-ink mb-2">Dados do certame</p>
      <div className="flex flex-col gap-2">
        <Form.Item
          label="Sigla"
          validateStatus={errors.abbreviation ? "error" : ""}
          help={errors.abbreviation?.message || " "}
        >
          <input
            {...register("abbreviation")}
            className="w-full border rounded-input px-3 py-2 h-[40px] bg-surface text-ink border-edge focus:outline-none focus:ring focus:ring-select/60"
          />
        </Form.Item>

        <Form.Item
          label="Descrição"
          validateStatus={errors.description ? "error" : ""}
          help={errors.description?.message || " "}
        >
          <input
            {...register("description")}
            className="w-full border rounded-input px-3 py-2 h-[40px] bg-surface text-ink border-edge focus:outline-none focus:ring focus:ring-select/60"
          />
        </Form.Item>

        <Form.Item label="Status">
          <Controller
            control={control}
            name="disabled"
            render={({ field }) => (
              <Switch
                checked={!field.value}
                onChange={(val) => field.onChange(!val)}
                checkedChildren="Ativo"
                unCheckedChildren="Desativado"
              />
            )}
          />
        </Form.Item>
      </div>
    </div>
  );

  const materiasVinculadas = (
    <div>
      <p className="text-lg font-semibold text-ink mb-1">
        Matérias vinculadas
      </p>
      <p className="text-sm text-ink-secondary mb-3">
        Questões e tags de cada matéria vinculada ao certame.
        {/* a contagem de questões é o total do certame — não há
            endpoint de contagem por matéria ainda (ver notas da
            tarefa) */}
      </p>

      <div className="flex flex-col gap-2 mb-3">
        {subjectIds.length === 0 && (
          <p className="text-sm text-ink-muted">Nenhuma matéria vinculada.</p>
        )}

        {subjectIds.map((subjectId) => {
          const materia = materiasById.get(subjectId);

          if (!materia) {
            return (
              <div
                key={subjectId}
                className="flex items-center justify-between gap-3 rounded-card border border-warn/40 bg-warn/[0.08] px-4 py-3"
              >
                <div>
                  <p className="text-sm font-semibold text-warn-ink">
                    Matéria não encontrada
                  </p>
                  <p className="text-xs text-ink-muted font-mono">
                    {abbreviateId(subjectId)}
                  </p>
                </div>
                <Button
                  danger
                  type="text"
                  size="small"
                  onClick={() => removeSubject(subjectId)}
                >
                  Remover
                </Button>
              </div>
            );
          }

          const tagCount = tagCountBySubject.get(subjectId) ?? 0;

          return (
            <div
              key={subjectId}
              className="flex items-center justify-between gap-3 rounded-card border border-edge bg-surface px-4 py-3"
            >
              <div>
                <p className="text-sm font-semibold text-ink">
                  {materia.name}
                </p>
                <p className="text-xs text-ink-secondary">
                  {questionCount == null
                    ? "— questões"
                    : `${numberFormatter.format(questionCount)} questões`}
                  {" · "}
                  {`${numberFormatter.format(tagCount)} tags`}
                </p>
              </div>
              <Button
                danger
                type="text"
                size="small"
                onClick={() => removeSubject(subjectId)}
              >
                Remover
              </Button>
            </div>
          );
        })}
      </div>

      {errors.subjectIds && (
        <div className="mb-3 text-danger-ink font-semibold bg-surface-muted border border-edge rounded-input px-2 py-1">
          {errors.subjectIds.message}
        </div>
      )}

      <Form.Item label="Adicionar matéria">
        <Select
          key={subjectIds.length}
          showSearch
          allowClear
          placeholder="Buscar matéria para adicionar"
          className="w-full"
          options={availableMaterias}
          filterOption={(input, option) =>
            String(option?.label ?? "")
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          onSelect={(value) => addSubject(String(value))}
        />
      </Form.Item>
    </div>
  );

  const bancasSection = (
    <div>
      <Form.Item
        label="Banca(s) do certame"
        extra="Vazio significa sem restrição: o aluno vê todas as bancas que tenham questão nas matérias deste certame. Inclua também a banca das questões autorais (Acertei), senão elas somem do filtro dele."
        validateStatus={errors.testingAgencyIds ? "error" : ""}
        help={errors.testingAgencyIds?.message}
      >
        <Controller
          control={control}
          name="testingAgencyIds"
          render={({ field }) => (
            <Select
              {...field}
              value={field.value ?? []}
              mode="multiple"
              showSearch
              className="w-full"
              placeholder="Nenhuma banca — sem restrição"
              options={(bancas ?? []).map((b: any) => ({
                label: `${b.year} - ${b.name}`,
                value: b.id,
              }))}
              filterOption={(input, option) =>
                String(option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          )}
        />
      </Form.Item>
    </div>
  );

  const tagsPanelHelpContent = (
    <div className="flex max-w-xs flex-col gap-2 text-[13px] text-ink-secondary">
      <p>
        As tags são unificadas automaticamente a partir das questões das matérias vinculadas a
        este certame — ninguém as cadastra manualmente aqui.
      </p>
      <p>
        <strong className="text-ink">Visível</strong> é mostrada ao aluno no filtro de Assunto;{" "}
        <strong className="text-ink">oculta</strong> fica escondida dele; e{" "}
        <strong className="text-ink">precisa de revisão</strong> sinaliza uma tag que parece
        duplicada ou um trecho de comentário salvo por engano como tag.
      </p>
      <p>
        Unificar tags duplicadas ou malformadas é feito na tela{" "}
        <strong className="text-ink">Revisar tags</strong> (<code>/certame/:id/tags</code>).
      </p>
    </div>
  );

  const tagsPanel = isNew ? null : (
    <FilterSection
      id="certame-tags-panel"
      icon={<Tags aria-hidden="true" />}
      accent="orange"
      title="Tags do certame"
      description="Saúde das tags que o aluno vê no filtro de Assunto, agrupadas por matéria."
      headerRight={
        <Popover content={tagsPanelHelpContent} title="Como funciona" trigger="click" placement="bottomRight">
          <button
            type="button"
            className="shrink-0 whitespace-nowrap rounded-chip px-2 py-1 text-[13px] font-medium text-ink-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-select/60 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          >
            <span aria-hidden="true">ⓘ</span> Como funciona
          </button>
        </Popover>
      }
    >
      {tagPanelLoading ? (
        <div className="flex items-center justify-center py-10">
          <Spin />
        </div>
      ) : !tagPanel ? (
        <p className="text-[13px] text-ink-muted">
          Não foi possível carregar o painel de tags.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {/* ── CONTADORES ── */}
          <div className="flex w-full flex-wrap gap-3">
            <TagCounterTile label="Tags únicas" value={tagPanel.totalUnique} />
            <TagCounterTile label="Visíveis" value={tagPanel.visibleCount} />
            <TagCounterTile label="Ocultas" value={tagPanel.hiddenCount} />
            <TagCounterTile
              label="Precisam de revisão"
              value={tagPanel.needsReviewCount}
              tone={tagPanel.needsReviewCount > 0 ? "warn" : "neutral"}
            />
          </div>

          <TagVisibilityBar
            visible={tagPanel.visibleCount}
            hidden={tagPanel.hiddenCount}
            needsReview={tagPanel.needsReviewCount}
            ariaLabel={`Todas as matérias: ${tagPanel.visibleCount} visíveis, ${tagPanel.hiddenCount} ocultas, ${tagPanel.needsReviewCount} precisam de revisão`}
          />

          {/* ── ALERTA DE TAGS SUSPEITAS ── */}
          {tagPanel.needsReviewCount > 0 && (
            <div className="flex flex-col gap-2 rounded-card border border-warn/[0.42] bg-warn/[0.08] px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-2">
                <TriangleAlert
                  className="mt-0.5 h-4 w-4 shrink-0 text-warn-ink"
                  aria-hidden="true"
                />
                <p className="text-[13px] text-warn-ink">
                  {`${numberFormatter.format(tagPanel.needsReviewCount)} tag${tagPanel.needsReviewCount === 1 ? "" : "s"} precisa${tagPanel.needsReviewCount === 1 ? "" : "m"} de revisão`}
                  {duplicateTagCount !== null && commentSnippetTagCount !== null
                    ? ` — ${numberFormatter.format(duplicateTagCount)} parece${duplicateTagCount === 1 ? "" : "m"} duplicada${duplicateTagCount === 1 ? "" : "s"} e ${numberFormatter.format(commentSnippetTagCount)} parece${commentSnippetTagCount === 1 ? "" : "m"} trecho de comentário.`
                    : "."}
                </p>
              </div>
              <Button size="small" onClick={() => navigate(`/certame/${id}/tags`)}>
                Revisar agora
              </Button>
            </div>
          )}

          {/* ── FILTRO + BUSCA ── */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <FilterChipGroup role="radiogroup" ariaLabel="Filtrar tags por estado">
              <FilterChip
                role="radio"
                label="Todas"
                selected={tagsFilterMode === "all"}
                onToggle={() => setTagsFilterMode("all")}
              />
              <FilterChip
                role="radio"
                label="Visíveis"
                selected={tagsFilterMode === "visible"}
                onToggle={() => setTagsFilterMode("visible")}
              />
              <FilterChip
                role="radio"
                label="Ocultas"
                selected={tagsFilterMode === "hidden"}
                onToggle={() => setTagsFilterMode("hidden")}
              />
              <FilterChip
                role="radio"
                label="Revisar"
                selected={tagsFilterMode === "review"}
                onToggle={() => setTagsFilterMode("review")}
              />
            </FilterChipGroup>

            <FilterSearch
              value={tagsSearch}
              onChange={setTagsSearch}
              placeholder="Buscar tag…"
              ariaLabel="Buscar tag"
              variant="section"
              className="sm:w-64"
            />
          </div>

          {/* ── POR MATÉRIA ── */}
          <div className="flex flex-col gap-3">
            {filteredTagsBySubject.length === 0 ? (
              <p className="text-[13px] text-ink-muted">
                Nenhuma tag encontrada para este filtro.
              </p>
            ) : (
              filteredTagsBySubject.map((subject) => {
                const fullSubject = tagPanel.bySubject.find(
                  (s) => s.subjectId === subject.subjectId,
                );
                const hasVisible = (fullSubject?.tags ?? []).some(
                  (t) => t.visible,
                );
                const hasHidden = (fullSubject?.tags ?? []).some(
                  (t) => !t.visible,
                );
                const subjectHiddenCount = (fullSubject?.tags ?? []).filter(
                  (t) => !t.visible,
                ).length;
                const subjectNeedsReviewCount = (
                  fullSubject?.tags ?? []
                ).filter((t) => t.needsReview).length;
                const subjectVisibleCount =
                  (fullSubject?.tags.length ?? 0) -
                  subjectHiddenCount -
                  subjectNeedsReviewCount;
                const isExpanded = expandedSubjectIds.has(subject.subjectId);
                const contentId = `certame-tags-subject-${subject.subjectId}`;
                const subjectTerm = (
                  subjectSearchTerms[subject.subjectId] ?? ""
                )
                  .trim()
                  .toLowerCase();
                // subject.tags já passou pelo filtro global (ver
                // filteredTagsBySubject) — a busca da matéria só estreita a
                // partir dele, nunca o substitui (design.md, Decisão 2).
                const searchedTags = subjectTerm
                  ? subject.tags.filter((tag) =>
                      tag.value.toLowerCase().includes(subjectTerm),
                    )
                  : subject.tags;

                return (
                  <div
                    key={subject.subjectId}
                    className="rounded-input border border-edge bg-surface-muted p-3"
                  >
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => toggleSubjectExpanded(subject.subjectId)}
                        aria-expanded={isExpanded}
                        aria-controls={contentId}
                        className="flex min-w-0 items-center gap-2 rounded-chip text-left"
                      >
                        <ChevronDown
                          className={`h-4 w-4 shrink-0 text-ink-muted transition-transform duration-150 ${isExpanded ? "rotate-180" : ""}`}
                          aria-hidden="true"
                        />
                        <p className="text-[13px] font-semibold text-ink">
                          {subject.subjectName}
                          <span className="ml-2 font-normal text-ink-muted">
                            {`${numberFormatter.format(subject.tags.length)} de ${numberFormatter.format(subject.totalTags)}`}
                          </span>
                        </p>
                      </button>
                      <Space size="small">
                        <Button
                          size="small"
                          disabled={!hasVisible}
                          onClick={() =>
                            setSubjectTagsVisibility(subject.subjectId, false)
                          }
                        >
                          Ocultar todas
                        </Button>
                        <Button
                          size="small"
                          disabled={!hasHidden}
                          onClick={() =>
                            setSubjectTagsVisibility(subject.subjectId, true)
                          }
                        >
                          Mostrar todas
                        </Button>
                      </Space>
                    </div>
                    <FilterSearch
                      value={subjectSearchTerms[subject.subjectId] ?? ""}
                      onChange={(value) =>
                        setSubjectSearchTerms((prev) => ({
                          ...prev,
                          [subject.subjectId]: value,
                        }))
                      }
                      placeholder={`Buscar tag em ${subject.subjectName}…`}
                      ariaLabel={`Buscar tag em ${subject.subjectName}`}
                      variant="section"
                      className="mb-2"
                    />
                    <TagVisibilityBar
                      className="mb-2"
                      visible={subjectVisibleCount}
                      hidden={subjectHiddenCount}
                      needsReview={subjectNeedsReviewCount}
                      ariaLabel={`${subject.subjectName}: ${subjectVisibleCount} visíveis, ${subjectHiddenCount} ocultas, ${subjectNeedsReviewCount} precisam de revisão`}
                    />
                    {isExpanded ? (
                      <div id={contentId} className="flex flex-wrap gap-2">
                        {searchedTags.map((tag) => (
                          <button
                            key={tag.value}
                            type="button"
                            aria-pressed={tag.visible}
                            onClick={() => toggleTagVisibility(tag)}
                            className={chipClassName({
                              selected: tag.needsReview || tag.visible,
                              tone: tag.needsReview ? "warn" : "select",
                            })}
                            title={
                              tag.needsReview
                                ? "Precisa de revisão"
                                : tag.visible
                                  ? "Visível — clique para ocultar"
                                  : "Oculta — clique para reexibir"
                            }
                          >
                            {tag.needsReview ? (
                              <TriangleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />
                            ) : null}
                            <span className="truncate">{tag.value}</span>
                            <span className="rounded-full bg-select/[0.14] px-2 py-0.5 text-[11px] font-semibold tabular-nums text-select-ink">
                              {numberFormatter.format(tag.count)}
                            </span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </FilterSection>
  );

  return (
    <DefaultLayout hideFilter>
      <div>
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Spin size="large" />
          </div>
        ) : (
          <Form layout="vertical">
            <CertameEditorShell
              header={header}
              unsavedBar={{
                count: unsavedChangesCount,
                onCancel: handleCancel,
                onSave: handleSubmit(onSubmit),
                cancelDisabled: cancelling,
                saveLoading: isSubmitting,
                saveLabel: isNew ? "Salvar" : "Salvar alterações",
              }}
              dadosDoCertame={dadosDoCertame}
              materiasVinculadas={materiasVinculadas}
              bancas={bancasSection}
            >
              {tagsPanel}
            </CertameEditorShell>
          </Form>
        )}
      </div>
    </DefaultLayout>
  );
}
